/**
 * Floating "Upravit stránku" button shown to tenant admins on every
 * public page (when not already in edit mode).
 *
 * Click → adds ``?_edit=1`` to the current URL, which trips
 * ``EditModeBootstrap`` and renders the bar + overlay.
 *
 * Hidden whenever the gate fails (anonymous, non-admin, or already
 * editing). Stays out of the way bottom-right; admin pages don't
 * mount this since the public chrome is suppressed under ``/admin/*``.
 */

import { type JSX } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useEditModeAccess } from "@/components/edit/useEditModeAccess";

export function EditModeFAB(): JSX.Element | null {
  const { t } = useTranslation("common");
  const { canEdit, isActive } = useEditModeAccess();
  const location = useLocation();
  const navigate = useNavigate();

  if (!canEdit || isActive) return null;

  function activate() {
    const params = new URLSearchParams(location.search);
    params.set("_edit", "1");
    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true },
    );
  }

  return (
    <button
      type="button"
      onClick={activate}
      data-testid="edit-mode-fab"
      aria-label={t("editPageAria")}
      // KAN-1819 — na telefonu jen kulatá ikona v rohu (44 px): popisek
      // překrýval hlavní CTA a cenu v konfigurátoru. Od `sm` plný tvar.
      className="fixed bottom-3 right-3 h-11 w-11 justify-center !p-0 sm:bottom-6 sm:right-6 sm:h-auto sm:w-auto sm:!px-4 sm:!py-2.5"
      style={{
        zIndex: 2147483645,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        borderRadius: 999,
        border: "1px solid #1d4ed8",
        background: "var(--accent-ink)",
        color: "white",
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 600,
        boxShadow: "0 6px 18px rgba(29, 78, 216, 0.32)",
        fontFamily: "inherit",
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
      <span className="sr-only sm:not-sr-only">{t("editPage")}</span>
    </button>
  );
}
